import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { 
  setExtractedData, 
  setOcrInputID, 
  setTaricJsonErrors, 
  clearTaricJsonErrors,
  fetchCreditStats,
  filterDeclarationsByErrors,
  setSuccessfulExpedientes,
  setErrorExpedientes,
  updateWorkflowSessionState
} from '../store/reducers';
import { apiFetch, refreshAccessToken } from '../utils/api';
import toast from 'react-hot-toast';
import { clearWorkflowSessionStorage } from '../utils/workflowSessionStorage';
import { readRowIndex } from '../utils/declarationKindUtils';

// Normalize to integer 0 or 1 for internal checks.
const normalizeExportacionBinary = (val) => {
  if (Array.isArray(val)) return val[0] === 1 ? 1 : 0;
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  return (!isNaN(num) && isFinite(num) && num === 1) ? 1 : 0;
};

// Coerce to number so JSON.stringify sends 0/1 not anything else
const asBinaryNumber = (n) => (n === 1 ? 1 : 0);

export const ensureTaricJsonBinaryFields = (taricJson) => {
  if (!taricJson?.taric?.duas?.exportaciones || !Array.isArray(taricJson.taric.duas.exportaciones)) return taricJson;
  const exportaciones = taricJson.taric.duas.exportaciones.map((exp) => {
    if (!exp || typeof exp !== 'object') return exp;
    // Strip binary fields from spread so we never pass through [0]; set them as integers only
    const {
      tiene_contenedores: _tc,
      estado_pdc: _ep,
      es_simplificado: _es,
      es_recapitulativo: _er,
      autorizacion_por_operacion: _ao,
      flag_datos_seguridad: _fd,
      completo: _c,
      es_plantilla: _ep2,
      ...rest
    } = exp;
    const tc = normalizeExportacionBinary(exp.tiene_contenedores);
    return {
      ...rest,
      tiene_contenedores: tc === 1 ? "1" : "0",
      estado_pdc: asBinaryNumber(normalizeExportacionBinary(exp.estado_pdc)),
      es_simplificado: asBinaryNumber(normalizeExportacionBinary(exp.es_simplificado)),
      es_recapitulativo: asBinaryNumber(normalizeExportacionBinary(exp.es_recapitulativo)),
      autorizacion_por_operacion: asBinaryNumber(normalizeExportacionBinary(exp.autorizacion_por_operacion)),
      flag_datos_seguridad: asBinaryNumber(normalizeExportacionBinary(exp.flag_datos_seguridad)),
      completo: asBinaryNumber(exp.completo !== undefined && exp.completo !== null ? normalizeExportacionBinary(exp.completo) : 1),
      es_plantilla: asBinaryNumber(exp.es_plantilla !== undefined && exp.es_plantilla !== null ? normalizeExportacionBinary(exp.es_plantilla) : 0),
    };
  });
  return {
    ...taricJson,
    taric: {
      ...taricJson.taric,
      duas: { ...taricJson.taric.duas, exportaciones },
    },
  };
};

// Helper function to play success sound (two-tone chime)
const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play two-tone chime (success sound)
    playTone(523.25, 0.2, 0); // C5
    playTone(659.25, 0.3, 150); // E5
  } catch (error) {
    console.error('Error playing success sound:', error);
  }
};

// Helper function to play error sound (softer, gentler tone)
const playErrorSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine'; // Softer sound instead of harsh sawtooth
        
        // Lower volume - start at 0.15 instead of 0.3
        gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play softer error sound (gentle descending tones)
    playTone(349.23, 0.2, 0); // F4 (softer than G4)
    playTone(293.66, 0.25, 150); // D4 (softer than D#4)
  } catch (error) {
    console.error('Error playing error sound:', error);
  }
};

export const useDocumentProcessing = () => {
  const dispatch = useDispatch();
  const store = useStore();
  
  // OPTIMIZED: Track stored ocrInputID to prevent unnecessary dispatches
  const storedOcrInputIdRef = useRef(null);
  const storedWorkflowSessionIdRef = useRef(null);
  
  // OPTIMIZED: Reusable helper to store ocrInputID (prevents duplicate code)
  const storeOcrInputId = useCallback((ocrInputId, activeWorkflowSessionId, source = 'unknown') => {
    if (!ocrInputId) return;
    
    const ocrInputIdStr = String(ocrInputId).trim();
    if (!ocrInputIdStr) return;
    
    // Only dispatch if value changed or workflow changed
    const hasChanged = ocrInputIdStr !== storedOcrInputIdRef.current || 
                      activeWorkflowSessionId !== storedWorkflowSessionIdRef.current;
    
    if (hasChanged) {
      // Batch updates: dispatch both in sequence (Redux batches automatically)
      dispatch(setOcrInputID(ocrInputIdStr));
      
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          ocrInputID: ocrInputIdStr,
        }));
      }
      
      // Update refs to prevent duplicate dispatches
      storedOcrInputIdRef.current = ocrInputIdStr;
      storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [dispatch]);
  
  // Get declarations from Redux to access expediente values
  const declarations = useSelector((state) => state.preparedJson?.declarations || []);
  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  
  // OPTIMIZED: Reset refs when workflow session changes (ensures updates work after switching workflows)
  useEffect(() => {
    if (activeWorkflowSessionId !== storedWorkflowSessionIdRef.current) {
      storedOcrInputIdRef.current = null;
      storedWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
  }, [activeWorkflowSessionId]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingTaric, setIsGeneratingTaric] = useState(false);
  const [hasGenerationError, setHasGenerationError] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState("");
  const [processingCancelled, setProcessingCancelled] = useState(false);

  const processDocuments = async (documents, onStatusUpdate, specifyAmount = [], userContext = '', kind = 'export') => {
    // CRITICAL: Don't make API call if there are no documents This prevents premature API calls when workflows are created without files
    if (!documents || documents.length === 0) {
      console.error(`❌ useDocumentProcessing [${activeWorkflowSessionId}] - No documents provided`);
      throw new Error('No PDF files uploaded. Please upload files before processing.');
    }
    
    setProcessingCancelled(false);
    // Clear all flags when starting new document processing to ensure fresh start
    sessionStorage.removeItem('documentReadyReceived');
    sessionStorage.removeItem('hasCompletedJob');
    
    // Clear per-workflow sessionStorage for the active workflow
    if (activeWorkflowSessionId) {
      clearWorkflowSessionStorage(activeWorkflowSessionId);
    }

    try {
      // CRITICAL: Filter to only actual File objects (workflow session state may have metadata)
      const validFiles = documents.filter(file => file instanceof File);
      
      if (validFiles.length === 0) {
        console.error(`❌ useDocumentProcessing [${activeWorkflowSessionId}] - No valid File objects found. Documents:`, documents);
        throw new Error('No PDF files uploaded. Please upload files before processing.');
      }
      
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });

      // Tell the backend which extraction profile to run (import vs export).
      // Defaults to export so existing flows are unchanged.
      const normalizedKind = kind === 'import' ? 'import' : 'export';
      formData.append('kind', normalizedKind);

      if (specifyAmount.length > 0) {
        formData.append('specifyAmount', JSON.stringify(specifyAmount));
      }

      if (userContext && typeof userContext === 'string' && userContext.trim().length > 0) {
        formData.append('user_context', userContext.trim());
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/items/ocr_json_input/generate`;

      // Use apiFetch for automatic token refresh on 401 errors
      let response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json'
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });


      // Parse response first to check for "already have a job" message
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        console.error(`❌ useDocumentProcessing [${activeWorkflowSessionId}] - Failed to parse response as JSON`, e);
        // If response is not JSON and not OK, throw error
        if (!response.ok) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }
        throw new Error('Invalid response from server');
      }

      // Handle "already have a job in the queue" error - should not happen with optimized backend But log it for debugging in case it does
      if ((!response.ok && (responseData.message?.includes('already have a job') || responseData.error?.includes('already have a job'))) ||
          (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue'))) {
        
        console.error(`❌ [useDocumentProcessing] Backend reports "already have a job in queue" - This should not happen with optimized backend!`, {
          workflowSessionId: activeWorkflowSessionId,
          message: responseData.message,
          error: responseData.error,
          status: responseData.status,
          code: responseData.code,
          fullResponse: responseData
        });
        
        // Throw error so user knows something went wrong With optimized backend, this should never happen as multiple jobs can run simultaneously
        throw new Error(responseData.message || responseData.error || 'Backend reported an existing job in queue. This should not happen with the optimized backend.');
      }

      // Check HTTP status code for other errors (not "already have a job")
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        errorMessage = responseData.message || responseData.error || responseData.detail || errorMessage;
        
        // Handle token expiration explicitly
        if (response.status === 401 || 
            errorMessage?.toLowerCase().includes('token expired') || 
            errorMessage?.toLowerCase().includes('expired')) {
          const refreshData = await refreshAccessToken();
          
          if (refreshData && refreshData.access_token) {
            // Retry the request with the new token
            response = await apiFetch(url, {
              method: 'POST',
              headers: {
                'accept': 'application/json'
              },
              body: formData
            }, false); // Don't retry again if this fails
            
            // Re-parse response after retry
            try {
              responseData = await response.json();
            } catch (e) {
              if (!response.ok) {
                throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
              }
              throw new Error('Invalid response from server');
            }
            
            // If retry still fails, throw the error
            if (!response.ok) {
              errorMessage = responseData.message || responseData.error || responseData.detail || `HTTP error! status: ${response.status}`;
              throw new Error(errorMessage);
            }
          } else {
            // Token refresh failed
            throw new Error('Token expired. Please log in again.');
          }
        } else {
          // Only log actual errors, not handled cases
          if (!responseData.message?.includes('already have a job') && !responseData.error?.includes('already have a job')) {
            console.error('Document processing request failed:', errorMessage);
          }
          throw new Error(errorMessage);
        }
      }

      // Check if the response indicates an error (not just "already have a job")
      if (responseData.status === 'error' && !responseData.message?.includes('already have a job in the queue')) {
        console.error('Document processing error:', responseData.message || responseData.error);
        throw new Error(responseData.message || responseData.error || 'Document processing failed');
      }
      
      // Log "already have a job" error if it still occurs (shouldn't with optimized backend)
      if (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue')) {
        console.error(`❌ [useDocumentProcessing] Backend error: "already have a job in queue"`, {
          workflowSessionId: activeWorkflowSessionId,
          message: responseData.message,
          code: responseData.code,
          status: responseData.status,
          fullResponse: responseData
        });
      }
      
      // Log "already have a job" error if it still occurs (shouldn't with optimized backend)
      if (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue')) {
        console.error(`❌ [useDocumentProcessing] Backend error: "already have a job in queue"`, {
          workflowSessionId: activeWorkflowSessionId,
          message: responseData.message,
          code: responseData.code,
          status: responseData.status
        });
      }

      // If we have a jobId in the response, notify stream to connect - stream will handle all status updates and data CRITICAL: Only accept jobId from API response, not from "already have a job" errors (handled above)
      if (responseData.jobId) {
        const jobId = responseData.jobId;
        
        // Immediately notify about the jobId so streaming can start The stream will handle all status updates, data extraction, and completion
        onStatusUpdate({
          jobId: jobId,
          processingStatus: 'processing',
          currentStep: 1,
          completedSteps: [0]
        });
      } else {
        console.warn(`⚠️ useDocumentProcessing [${activeWorkflowSessionId}] - No jobId in response`, {
          responseData
        });
      }

      return responseData;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return error.response;
    }
  };

  const generateTaricDeclaration = async (preparedJson) => {
    try {
      setIsGeneratingTaric(true);
      setHasGenerationError(false);
      setGenerationErrorMessage("");
      dispatch(clearTaricJsonErrors());
      
      // Debug: Check environment variable
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        throw new Error('API configuration error: Missing API base URL');
      }

      // Try with trailing slash (consistent with GET requests)
      const url = `${apiBaseUrl}/api/items/taric_json_output/`;
      const bodyToSend = ensureTaricJsonBinaryFields(preparedJson?.taricJson || {});
      
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyToSend)
      });

      const responseData = await response.json();

      // CRITICAL: Handle null/undefined responseData
      if (!responseData || typeof responseData !== 'object') {
        setHasGenerationError(true);
        setGenerationErrorMessage("Invalid response from server. Please try again.");
        playErrorSound();
        dispatch(setSuccessfulExpedientes([]));
        dispatch(setErrorExpedientes([]));
        dispatch(clearTaricJsonErrors());
        return { success: false, data: responseData };
      }

      // OPTIMIZED: Extract and store ocr_input_id from API response per workflow
      if (responseData.ocr_input_id && activeWorkflowSessionId) {
        storeOcrInputId(responseData.ocr_input_id, activeWorkflowSessionId, 'generateTaricDeclaration');
      }

      if (responseData.status === 'error' && responseData.message) {
        setHasGenerationError(true);
        setGenerationErrorMessage(responseData.message || "An error occurred while generating the Taric declaration.");
        playErrorSound();
        dispatch(setSuccessfulExpedientes([]));
        dispatch(setErrorExpedientes([]));
        dispatch(clearTaricJsonErrors());
        return { success: false, data: responseData };
      }

      // Dispatch errors to Redux - reducer handles all filtering (declarations, clientes, remitentes, agentes, taricJson) CRITICAL: Safely handle null/undefined responseData properties
      const errors = (responseData && Array.isArray(responseData.errors)) ? responseData.errors : [];
      const success = (responseData && Array.isArray(responseData.success)) ? responseData.success : [];
      const hasErrors = errors.length > 0;
      const hasSuccess = success.length > 0;

      // Always dispatch to trigger filtering (reducer handles success/error logic)
      if (hasErrors || hasSuccess) {
        const successIndices = new Set(
          success.map(item => readRowIndex(item)).filter(idx => typeof idx === 'number' && idx >= 0)
        );
        // CRITICAL: If there are no success indices (only errors), keep all indices Only filter when there are actually success indices to remove
        const indicesToKeep = successIndices.size > 0 && declarations.length > 0
          ? Array.from({ length: declarations.length }, (_, i) => i).filter(idx => !successIndices.has(idx))
          : (declarations.length > 0 ? Array.from({ length: declarations.length }, (_, i) => i) : []);
        
        // Filter workflow session state BEFORE dispatching (so Dashboard uses filtered data) Only filter when there are actually items to remove (success indices exist)
        if (activeWorkflowSessionId && successIndices.size > 0 && indicesToKeep.length < declarations.length) {
          const currentState = store.getState();
          const currentWorkflowSession = currentState?.workflowSessions?.sessions?.[activeWorkflowSessionId];
          const workflowState = currentWorkflowSession?.state;
          
          if (workflowState) {
            // CRITICAL: Preserve existing arrays if they exist, only filter if they're arrays Don't default to [] if undefined/null - preserve the existing state
            const existingSelectedSenders = Array.isArray(workflowState.selectedSender) ? workflowState.selectedSender : (workflowState.selectedSender || []);
            const existingSelectedReceivers = Array.isArray(workflowState.selectedReceiver) ? workflowState.selectedReceiver : (workflowState.selectedReceiver || []);
            const existingSelectedAgents = Array.isArray(workflowState.selectedAgent) ? workflowState.selectedAgent : (workflowState.selectedAgent || []);
            
            // Filter arrays by index (only keep indices that match indicesToKeep)
            const filteredSelectedSenders = existingSelectedSenders.filter((_, idx) => indicesToKeep.includes(idx));
            const filteredSelectedReceivers = existingSelectedReceivers.filter((_, idx) => indicesToKeep.includes(idx));
            const filteredSelectedAgents = existingSelectedAgents.filter((_, idx) => indicesToKeep.includes(idx));
            
            // Filter editableData/extractedData
            const workflowEditableData = Array.isArray(workflowState.editableData) ? workflowState.editableData : [];
            const workflowExtractedData = Array.isArray(workflowState.extractedData) ? workflowState.extractedData : [];
            const filteredEditableData = workflowEditableData.filter((_, idx) => indicesToKeep.includes(idx));
            const filteredExtractedData = workflowExtractedData.filter((_, idx) => indicesToKeep.includes(idx));
            
            // Update workflow session state immediately (synchronously) CRITICAL: Always update with filtered arrays (even if empty) to maintain index correspondence
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
              selectedSender: filteredSelectedSenders,
              selectedReceiver: filteredSelectedReceivers,
              selectedAgent: filteredSelectedAgents,
              editableData: filteredEditableData.length > 0 ? filteredEditableData : workflowState.editableData,
              extractedData: filteredExtractedData.length > 0 ? filteredExtractedData : workflowState.extractedData,
            }));
          }
        }
        // CRITICAL: If there are only errors (no success), don't filter - preserve existing selections The condition above only runs when successIndices.size > 0 && indicesToKeep.length < declarations.length
        
        // CRITICAL: Store TARIC JSON errors ONLY in workflow session state (per-workflow) Also update global preparedJson for reducer logic (filtering), but errors are workflow-only
        const currentState = store.getState();
        const currentActiveWorkflowSessionId = currentState?.workflowSessions?.activeSessionId;
        
        // Update global preparedJson for filtering logic (reducer needs it)
        dispatch(setTaricJsonErrors(responseData));
        
        // Filter documents state (extractedData, editableData) - will sync to workflow session below
        if (indicesToKeep.length < declarations.length) {
          dispatch(filterDeclarationsByErrors(indicesToKeep));
        }
        
        // CRITICAL: Store taricJsonErrors in workflow session with exportacionesIndex re-mapped to match filtered state.
        // After removing successful exports, the remaining export (e.g. originally index 1) is now at index 0 in the UI.
        // So errors must use the new index so "Export #1" and field IDs (e.g. codigo_un_1) match the form and scroll works.
        if (currentActiveWorkflowSessionId) {
          let taricJsonErrorsToStore = responseData;
          if (successIndices.size > 0 && indicesToKeep.length < declarations.length && Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
            const oldToNewMap = new Map(indicesToKeep.map((oldIdx, newIdx) => [oldIdx, newIdx]));
            const transformedErrors = responseData.errors.map((err) => {
              const originalIndex = readRowIndex(err);
              const newIndex = oldToNewMap.has(originalIndex) ? oldToNewMap.get(originalIndex) : originalIndex;
              // Rewrite under whichever index key the API used (import vs export).
              const idxKey = err.importacionesIndex !== undefined ? 'importacionesIndex' : 'exportacionesIndex';
              return { ...err, [idxKey]: newIndex };
            });
            taricJsonErrorsToStore = { ...responseData, errors: transformedErrors };
          }
          setTimeout(() => {
            const updatedState = store.getState();
            const updatedPreparedJson = updatedState.preparedJson;
            
            if (updatedPreparedJson) {
              dispatch(updateWorkflowSessionState(currentActiveWorkflowSessionId, {
                preparedJson: updatedPreparedJson,
                taricJsonErrors: taricJsonErrorsToStore,
              }));
            }
          }, 50);
        }
      }

      // Handle errors
      if (hasErrors) {
        // Set error state so modal shows error
        setHasGenerationError(true);
        setGenerationErrorMessage("Some declarations have validation errors. Please check the details.");
        
        // Play error sound
        playErrorSound();
        
        // Extract error expedientes and store in Redux
        const errorExpedientes = errors.map(item =>
          item.expediente || `#${(readRowIndex(item) || 0) + 1}`
        );
        dispatch(setErrorExpedientes(errorExpedientes));

        // expand field overview panel when closing generating modal so user can see errors
        window.dispatchEvent(new CustomEvent('fieldOverviewCollapse', {
          detail: { collapsed: false, expandContent: true },
        }));

        // rxpand all declaration summary exports so user can see errors per export
        if (activeWorkflowSessionId && declarations.length > 0) {
          const declarationSummaryCollapsedByIndex = {};
          for (let i = 0; i < declarations.length; i++) {
            declarationSummaryCollapsedByIndex[i] = false;
          }
          dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { declarationSummaryCollapsedByIndex }));
        }
      } else {
        // Clear error state if no errors
        setHasGenerationError(false);
        setGenerationErrorMessage("");
        dispatch(setErrorExpedientes([]));
      }

      // Handle success - only show success toasts if there are successful items
      if (hasSuccess) {
        // Play success sound
        playSuccessSound();
        
        // Extract successful expedientes and store in Redux
        const successfulExpedientes = responseData.success.map(item =>
          item.expediente || `#${(readRowIndex(item) || 0) + 1}`
        );
        dispatch(setSuccessfulExpedientes(successfulExpedientes));
        
        // CRITICAL: If there are both success and errors, log that successful ones were removed Errors are handled above
      } else {
        dispatch(setSuccessfulExpedientes([]));
      }

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to process Taric declaration');
      }

      // CRITICAL: If no expedientes were created (neither success nor errors), treat as error This prevents showing success modal when nothing was actually generated
      if (!hasSuccess && !hasErrors) {
        setHasGenerationError(true);
        setGenerationErrorMessage("No declarations were generated. Please check your data and try again.");
        playErrorSound();
        dispatch(setErrorExpedientes([]));
        dispatch(setSuccessfulExpedientes([]));
        return { success: false, data: responseData };
      }

      // Return success based on whether there are errors
      const isSuccessful = !hasErrors;
      return { success: isSuccessful, data: responseData };
    } catch (error) {
      setHasGenerationError(true);
      setGenerationErrorMessage(error.message || "An error occurred while generating the Taric declaration.");
      
      // Play error sound for catch errors
      playErrorSound();
      
      return { success: false, error };
    } finally {
      setIsGeneratingTaric(false);
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    isGeneratingTaric,
    hasGenerationError,
    generationErrorMessage,
    processingCancelled,
    setProcessingCancelled,
    processDocuments,
    generateTaricDeclaration
  };
}; 